import React from 'react';
import { Box, Chip, LinearProgress, Skeleton, Stack, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useChatUsage } from '../hooks/useChat';
import { ChatUsageLimit } from '../types/api';
import {
  PERIOD_LABELS,
  USAGE_WARNING_RATIO,
  formatCredits,
  formatReset,
  usedRatio,
} from '../utils/chatUsage';

/**
 * One limit, e.g.
 *   This week                    90 of 500 available
 *   [█████████████████░░░]                   (orange from 80% used)
 *   Resets Mon, Oct 5, 5:30 AM
 */
function LimitRow({ limit, available }: { limit: ChatUsageLimit; available: boolean }) {
  const ratio = usedRatio(limit);
  const warn = !available || ratio >= USAGE_WARNING_RATIO;
  const reset = formatReset(limit.resets_at);
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
        <Typography variant="body2">{PERIOD_LABELS[limit.period]}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          <Box component="span" sx={{ color: warn ? 'warning.main' : 'text.primary', fontWeight: 600 }}>
            {formatCredits(limit.remaining)}
          </Box>{' '}
          of {formatCredits(limit.limit)} available
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={ratio * 100}
        color={warn ? 'warning' : 'primary'}
        aria-label={`${PERIOD_LABELS[limit.period]}: ${formatCredits(limit.used)} of ${formatCredits(limit.limit)} credits used`}
        sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
      />
      {reset && available && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
          Resets {reset}
        </Typography>
      )}
    </Box>
  );
}

/** A short warning line under the limits (why there are no credits, or that the app is paused). */
function Note({ children }: { children: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ color: 'warning.dark' }}>
      <InfoOutlinedIcon sx={{ fontSize: 16, mt: '2px' }} />
      <Typography variant="caption">{children}</Typography>
    </Stack>
  );
}

/**
 * Assistant credits per configured limit, for the account menu (below Profile).
 * Shows one row per limit the user's plan has in chat_usage.json: a Free user
 * with only a weekly limit sees just "This week". An unverified user sees
 * "All time: 0 of 0 available" plus the plan's reason, e.g. "No credits
 * available. Verify your email address to use the assistant."
 * Loaded when the menu opens (the menu only mounts its content while open).
 */
const CreditUsageSection: React.FC = () => {
  const { data: usage, isLoading, isError } = useChatUsage();

  return (
    <Box sx={{ px: 2, pt: 1.25, pb: 1.5, width: 280 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Assistant credits
        </Typography>
        {usage && <Chip size="small" label={usage.plan.name} variant="outlined" />}
      </Stack>

      {isLoading ? (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={34} />
          <Skeleton variant="rounded" height={34} />
        </Stack>
      ) : isError || !usage ? (
        <Typography variant="caption" color="text.secondary">
          Couldn't load your credit usage.
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {usage.limits.length === 0 ? (
            <Typography variant="body2">No credit limits on your plan.</Typography>
          ) : (
            usage.limits.map((limit) => <LimitRow key={limit.period} limit={limit} available={usage.available} />)
          )}
          {!usage.available && (
            <Note>
              No credits available. {usage.unavailable_message ?? "The assistant isn't available on your plan."}
            </Note>
          )}
          {usage.available && usage.global_limit_reached && (
            <Note>The assistant has reached its overall usage limit and is paused for now.</Note>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default CreditUsageSection;
